import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import type { SortDir } from '../api/searchTypes';

// One column title of a results header, shared by the event and the trader
// header: an arrow on the active sort, and the arrow's width kept free when
// inactive.
export interface SortableHeaderCellProps {
    label: string;
    align?: 'left' | 'right';
    sortKey?: string;
    sortable: boolean;
    active: boolean;
    dir: SortDir;
    info: string;
    unsortableTitle?: string;
    onSort: (key: string) => void;
}

function Indicator({
    active,
    dir,
}: Pick<SortableHeaderCellProps, 'active' | 'dir'>) {
    if (!active) return <Box sx={{ width: 11 }} />;
    return dir === 'desc' ? (
        <ArrowDownwardIcon sx={{ fontSize: 11 }} />
    ) : (
        <ArrowUpwardIcon sx={{ fontSize: 11 }} />
    );
}

export function SortableHeaderCell({
    label,
    align,
    sortKey,
    sortable,
    active,
    dir,
    info,
    unsortableTitle = '',
    onSort,
}: SortableHeaderCellProps) {
    const theme = useTheme();
    return (
        <Tooltip
            title={sortable ? info : unsortableTitle}
            placement="top"
            disableInteractive
        >
            <Box
                onClick={() => sortable && sortKey && onSort(sortKey)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                        align === 'left' ? 'flex-start' : 'flex-end',
                    gap: '2px',
                    cursor: sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    color: active ? 'primary.main' : 'text.disabled',
                    '&:hover': sortable
                        ? { color: alpha(theme.palette.primary.main, 0.8) }
                        : undefined,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: active ? 700 : 500,
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                        color: 'inherit',
                    }}
                >
                    {label.toUpperCase()}
                </Typography>
                <Indicator active={active} dir={dir} />
            </Box>
        </Tooltip>
    );
}
